import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Inbox, MessageSquare, FileText, Clock, ShoppingBag, AlertTriangle, Bell, RefreshCw, ChevronRight, Loader2, CheckCircle2, Sparkles, TrendingUp, Handshake, Zap, PhoneMissed, Percent, Banknote, Gauge, CalendarClock, ClipboardList, Pencil, UserPlus, User, Users, ClipboardCheck, Wrench, Bike } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import AssignTaskDialog from "./AssignTaskDialog";
import AssignToSellerButton from "./AssignToSellerButton";

const ICON_MAP = {
    unread_messages: MessageSquare,
    pending_offers: ShoppingBag,
    pending_reservations: Clock,
    paid_instant_buys: Zap,
    missed_calls: PhoneMissed,
    open_buy_requests: ShoppingBag,
    stale_listings: AlertTriangle,
    missing_documents: FileText,
    saved_search_hits: Bell,
    top_reactivation: TrendingUp,
    referral_commission_proposal: Handshake,
    skonto_expiring: Percent,
    open_manufacturer_payments: Banknote,
    vorfuehr_km_update: Gauge,
    tuev_expiring: CalendarClock,
    offer_followup: CalendarClock,
    lead_followup: CalendarClock,
    pending_testrides: Bike,
    guest_leads: UserPlus,
    incomplete_listings: ClipboardList,
    delegated_overdue: AlertTriangle,
    delegated_done: Users,
    dispo_todos: ClipboardCheck,
    workshop_due_soon: Wrench,
    workorder_reschedule_approval: CalendarClock,
    standzeit_warning: Clock,
    sofortmitnahme_approval: Zap,
    broken_links: AlertTriangle,
    social_approval_pending: Sparkles,
};

const SEVERITY_STYLES = {
    critical: { bg: "bg-red-50", border: "border-red-300", text: "text-red-900", pill: "bg-red-600 text-white", icon: "text-red-600" },
    warning: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-900", pill: "bg-amber-500 text-white", icon: "text-amber-600" },
    info: { bg: "bg-[#FFCC00]/10", border: "border-[#FFCC00]", text: "text-gray-900", pill: "bg-[#111827] text-[#FFCC00]", icon: "text-[#111827]" },
};

/**
 * Feb-2026 — Smart-Inbox: Aggregierte „Aktion erforderlich"-Liste als
 * neuer Default-Tab im Dashboard. Reduziert die kognitive Last von 11
 * Tabs auf 1 priorisierte To-Do-Liste.
 */
export default function SmartInbox() {
    const { user, refresh } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activating, setActivating] = useState(false);
    const [assignItem, setAssignItem] = useState(null);
    const [dispoActiveId, setDispoActiveId] = useState(null);
    const [dispoDate, setDispoDate] = useState("");
    const [dispoBusy, setDispoBusy] = useState(null);
    const navigate = useNavigate();

    // Feb-2026 (User-Wunsch) — Smart-Inbox startet für Händler (B2B) DEAKTIVIERT
    // und muss im Händlerprofil manuell aktiviert werden. Andere Rollen (B2C,
    // Vermittler) sind unverändert immer aktiv.
    const isDealer = user?.role === "b2b";
    const enabled = true;  // Jun-2026 (User-Wunsch): Smart-Inbox immer aktiv, nicht abschaltbar.

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/dashboard/inbox");
            setData(r.data);
        } catch {
            /* ignore */
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        const t = setTimeout(() => { if (enabled) load(); else setLoading(false); }, 0);
        return () => clearTimeout(t);
    }, [enabled]);

    const activate = async () => {
        setActivating(true);
        try {
            await api.patch("/users/me/dealer-profile", { smart_inbox_enabled: true });
            await refresh();
            toast.success("Smart-Inbox aktiviert");
        } catch {
            toast.error("Aktivieren fehlgeschlagen");
        } finally {
            setActivating(false);
        }
    };

    const dismissFollowup = async (id) => {
        try {
            await api.delete(`/me/offer-followups/${id}`);
            toast.success("Wiedervorlage erledigt");
            await load();
        } catch {
            toast.error("Konnte nicht als erledigt markiert werden");
        }
    };

    // Aug-2026 (User-Wunsch) — Probefahrt-Anfragen einzeln listen & einzeln zuweisen.
    const [employees, setEmployees] = useState([]);
    const [trBusy, setTrBusy] = useState(null);
    useEffect(() => {
        if (isDealer && data?.items?.some((i) => i.type === "pending_testrides")) {
            api.get("/me/tasks/assignable-employees").then((r) => setEmployees(r.data.items || [])).catch(() => {});
        }
    }, [isDealer, data]);
    const assignTestRide = async (sub, sellerId) => {
        setTrBusy(sub.id);
        try {
            const r = await api.patch(`/test-ride-requests/${sub.id}/assign`, { seller_id: sellerId || null });
            toast.success(sellerId ? `Zugewiesen an ${r.data.assigned_seller_name}` : "Zuweisung aufgehoben");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zuweisen fehlgeschlagen");
        } finally { setTrBusy(null); }
    };
    const testRideStatus = async (sub, status) => {
        setTrBusy(sub.id);
        try {
            await api.patch(`/test-ride-requests/${sub.id}/status`, { status });
            toast.success("Status aktualisiert");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
        } finally { setTrBusy(null); }
    };

    // Jun-2026 (User-Wunsch) — Pagination für unvollständige Inserate (10/20/50, älteste zuerst).
    const [incPageSize, setIncPageSize] = useState(10);
    const [incPage, setIncPage] = useState(0);
    // Jul-2026 (User-Wunsch) — unvollständiges Inserat einem Mitarbeiter zuweisen.
    const [assignees, setAssignees] = useState([]);
    const [assignBusy, setAssignBusy] = useState(null);
    useEffect(() => {
        api.get("/me/tasks/assignable-employees")
            .then((r) => setAssignees(r.data?.items || []))
            .catch(() => setAssignees([]));
    }, []);
    const assignIncomplete = async (sub, empId) => {
        if (!empId) return;
        const emp = assignees.find((a) => a.id === empId);
        setAssignBusy(sub.listing_id);
        try {
            await api.post("/dealer/tasks", {
                assignee_id: empId,
                area: "Verkauf",
                title: `Inserat vervollständigen: ${sub.label}`,
                body: `Fehlende Angaben: ${(sub.missing || []).join(", ")}`,
                cta_url: `/edit-listing/${sub.listing_id}`,
            });
            toast.success(`Aufgabe an ${emp?.name || "Mitarbeiter"} zugewiesen`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zuweisung fehlgeschlagen");
        } finally {
            setAssignBusy(null);
        }
    };

    // Jul-2026 (User-Wunsch) — ungelesene Nachricht einem Mitarbeiter zuweisen.
    const [msgAssignBusy, setMsgAssignBusy] = useState(null);
    const assignMessage = async (sub, empId) => {
        if (!empId) return;
        const emp = assignees.find((a) => a.id === empId);
        setMsgAssignBusy(sub.id);
        try {
            await api.post("/dealer/tasks", {
                assignee_id: empId,
                area: "Verkauf",
                title: `Nachricht beantworten: ${sub.label}`,
                body: sub.snippet ? `Letzte Nachricht: „${sub.snippet}“` : "",
                source_type: "unread_message",
                cta_url: `/dashboard?tab=messages&thread=${sub.thread_id}`,
            });
            toast.success(`Aufgabe an ${emp?.name || "Mitarbeiter"} zugewiesen`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zuweisung fehlgeschlagen");
        } finally {
            setMsgAssignBusy(null);
        }
    };

    // Jun-2026 (User-Wunsch) — unvollständiges Inserat 2 Tage / 1 Woche schlummern.
    const snoozeIncomplete = async (listingId, days) => {
        try {
            await api.post(`/listings/${listingId}/incomplete-snooze`, { days });
            toast.success(days === 2 ? "Hinweis für 2 Tage geschlummert" : "Hinweis für 1 Woche geschlummert");
            await load();
        } catch {
            toast.error("Schlummern fehlgeschlagen");
        }
    };

    // Jun-2026 (User-Wunsch) — Händler nimmt erledigte Mitarbeiter-Aufgabe zur Kenntnis.
    const ackTask = async (taskId) => {
        try {
            await api.post(`/dealer/tasks/${taskId}/ack`);
            await load();
        } catch {
            toast.error("Aktion fehlgeschlagen");
        }
    };

    // Jun-2026 (User-Wunsch) — Kundendienst-Freigabe für Terminverschiebungen.
    const [apprActiveId, setApprActiveId] = useState(null);
    const [apprReason, setApprReason] = useState("");
    const [apprDate, setApprDate] = useState("");
    const [apprBusy, setApprBusy] = useState(null);
    // Jul-2026 (User-Wunsch) — Standgebühren-Rechnung per Klick + Sofortmitnahme-Freigabe
    const [standBusy, setStandBusy] = useState(null);
    const [sofortBusy, setSofortBusy] = useState(null);
    const createStandInvoice = async (sub) => {
        setStandBusy(sub.id);
        try {
            const { data } = await api.post(`/work-orders/${sub.id}/standgebuehr-invoice`);
            toast.success(`Standgebühren-Rechnung (Entwurf) über ${Number(data.total_gross || 0).toFixed(2).replace(".", ",")} € erstellt`);
            navigate("/dealer/invoices");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Rechnung konnte nicht erstellt werden");
        } finally { setStandBusy(null); }
    };
    const decideSofort = async (sub, approve) => {
        setSofortBusy(sub.id);
        try {
            await api.post(`/listings/${sub.listing_id}/sofortmitnahme/decide`, { approve });
            toast.success(approve ? "Sofortmitnahme freigegeben" : "Sofortmitnahme abgelehnt");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Entscheidung fehlgeschlagen");
        } finally { setSofortBusy(null); }
    };
    // Jul-2026 (User-Wunsch) — Link-Reparatur direkt aus dem Hinweis
    const [linkEditId, setLinkEditId] = useState(null);
    const [linkNewUrl, setLinkNewUrl] = useState("");
    const [linkBusy, setLinkBusy] = useState(null);
    const fixLink = async (sub, remove) => {
        setLinkBusy(sub.id);
        try {
            await api.post(`/broken-links/${sub.id}/fix`, { new_url: remove ? null : linkNewUrl.trim(), remove });
            toast.success(remove ? "Link entfernt" : "Link korrigiert — neuer Link ist erreichbar");
            setLinkEditId(null);
            setLinkNewUrl("");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Korrektur fehlgeschlagen");
        } finally { setLinkBusy(null); }
    };
    const openApproval = (sub) => {
        if (apprActiveId === sub.id) { setApprActiveId(null); return; }
        setApprActiveId(sub.id);
        setApprReason(sub.reason || "");
        setApprDate(sub.new_date || "");
    };
    const approveReschedule = async (sub) => {
        if (!apprReason.trim()) { toast.error("Bitte Grund bestätigen oder ergänzen"); return; }
        setApprBusy(sub.id);
        try {
            await api.post(`/work-orders/${sub.id}/reschedule-approval/approve`,
                { reason: apprReason.trim(), new_date: apprDate || undefined });
            toast.success("Freigegeben · Kunde wird informiert (E-Mail/WhatsApp/SMS)");
            setApprActiveId(null);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Freigabe fehlgeschlagen"); }
        finally { setApprBusy(null); }
    };
    const dismissReschedule = async (sub) => {
        setApprBusy(sub.id);
        try {
            await api.post(`/work-orders/${sub.id}/reschedule-approval/dismiss`, {});
            toast.success("Ohne Kundeninfo verworfen");
            setApprActiveId(null);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen"); }
        finally { setApprBusy(null); }
    };

    // Jul-2026 (User-Wunsch) — Dispo-To-Do schlummern (2 Tage / 1 Woche).
    const dispoSnooze = async (todo, days) => {
        setDispoBusy(todo.id);
        try {
            await api.post("/dealer/dispo/todos/snooze", { todo_id: todo.id, days });
            toast.success(`Erinnerung schlummert ${days === 7 ? "1 Woche" : `${days} Tage`}`);
            await load();
        } catch {
            toast.error("Schlummern fehlgeschlagen");
        } finally {
            setDispoBusy(null);
        }
    };

    // Jun-2026 (User-Wunsch) — Dispo-To-Do direkt aus der Inbox erledigen.
    const dispoConfirm = async (todo) => {
        setDispoBusy(todo.id);
        try {
            if (todo.action === "deregister") {
                await api.patch(`/listings/${todo.listing_id}/dispo/tradein-deregister`, dispoDate ? { date: dispoDate } : {});
                toast.success("Fahrzeug als abgemeldet markiert");
            } else if (todo.action === "hu_renew") {
                if (!dispoDate) { toast.error("Bitte neues HU/TÜV-Datum wählen"); setDispoBusy(null); return; }
                await api.patch(`/listings/${todo.listing_id}/dispo/hu-renew`, { valid_until: dispoDate });
                toast.success("Neues HU/TÜV-Datum gespeichert");
            }
            setDispoActiveId(null);
            setDispoDate("");
            await load();
        } catch {
            toast.error("Aktion fehlgeschlagen");
        } finally {
            setDispoBusy(null);
        }
    };

    // Jun-2026 (User-Wunsch) — Hersteller-Aktion direkt verlängern (bis Monatsende).
    const extendBonus = async (todo) => {
        setDispoBusy(todo.id);
        try {
            await api.post(`/dealer/bonus-types/${todo.bonus_type_id}/extend`);
            toast.success("Hersteller-Aktion bis Monatsende verlängert");
            await load();
        } catch {
            toast.error("Verlängern fehlgeschlagen");
        } finally {
            setDispoBusy(null);
        }
    };
    // Aug-2026 (User-Report Jörg) — veralteten Werkstatt-Termin-Hinweis direkt
    // auflösen (z. B. wenn der Termin nicht mehr existiert).
    const resolveWorkshopDue = async (sub) => {
        setDispoBusy(sub.id);
        try {
            await api.post(`/work-orders/${sub.id}/completion/resolve`);
            toast.success("Termin-Hinweis aufgelöst");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Auflösen fehlgeschlagen");
        } finally {
            setDispoBusy(null);
        }
    };

    if (!enabled) {        return (
            <div className="bg-gradient-to-br from-[#FFCC00]/10 to-white border-2 border-[#FFCC00] rounded-sm p-12 text-center" data-testid="inbox-disabled">
                <Zap className="w-12 h-12 text-[#FFCC00] mx-auto mb-3" />
                <h3 className="font-display font-black text-xl mb-1">Smart-Inbox ist deaktiviert</h3>
                <p className="text-sm text-gray-600 max-w-md mx-auto mb-5">
                    Die Smart-Inbox bündelt alle offenen Aufgaben (neue Nachrichten, Reservierungen,
                    fehlende Belege, stale Inserate) in einer priorisierten Liste. Aktiviere sie, um
                    auf einen Blick zu sehen, was zu tun ist.
                </p>
                <button
                    type="button"
                    onClick={activate}
                    disabled={activating}
                    className="inline-flex items-center gap-2 bg-[#111827] hover:bg-black text-[#FFCC00] font-bold uppercase tracking-wider px-5 py-2.5 rounded-sm disabled:opacity-50"
                    data-testid="inbox-activate-btn"
                >
                    {activating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                    Jetzt aktivieren
                </button>
                <p className="text-[11px] text-gray-400 mt-3">
                    Du kannst sie jederzeit im Händlerprofil unter „Smart-Inbox“ wieder deaktivieren.
                </p>
            </div>
        );
    }

    if (loading) {
        return <div className="bg-white border border-gray-200 rounded-sm p-12 flex items-center justify-center gap-2 text-gray-500"><Loader2 className="w-5 h-5 animate-spin" /> Lade Inbox…</div>;
    }
    if (!data || !data.items || data.items.length === 0) {
        return (
            <div className="bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-300 rounded-sm p-12 text-center" data-testid="inbox-empty">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <h3 className="font-display font-black text-xl mb-1">Alles erledigt! 🎉</h3>
                <p className="text-sm text-gray-600">Keine offenen Aktionen — du bist auf dem aktuellen Stand.</p>
                <button onClick={load} className="mt-4 text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-[#111827] inline-flex items-center gap-1">
                    <RefreshCw className="w-3 h-3" /> Aktualisieren
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-3" data-testid="smart-inbox">
            <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                    <Inbox className="w-5 h-5 text-[#111827]" />
                    <h2 className="font-display font-black text-xl">Was zu tun ist</h2>
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-[#111827] text-[#FFCC00] px-2 py-0.5 rounded-sm font-mono-num">
                        {data.total}
                    </span>
                </div>
                <button onClick={load} className="text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-[#111827] inline-flex items-center gap-1" data-testid="inbox-refresh">
                    <RefreshCw className="w-3 h-3" /> Aktualisieren
                </button>
            </div>

            {data.items.map((item) => {
                const Icon = ICON_MAP[item.type] || Sparkles;
                const isReactivation = item.type === "top_reactivation";
                const sty = isReactivation
                    ? { bg: "bg-gradient-to-r from-[#FFCC00]/20 via-emerald-50 to-white", border: "border-emerald-400", text: "text-emerald-900", pill: "bg-emerald-600 text-white", icon: "text-emerald-700" }
                    : SEVERITY_STYLES[item.severity] || SEVERITY_STYLES.info;

                // Jun-2026 — Angebots-Wiedervorlagen: eigene Karte mit „Erledigt"-Aktion
                // je Eintrag (kein verschachteltes Button-in-Button).
                if (item.type === "offer_followup") {
                    return (
                        <div
                            key={item.type}
                            className={`w-full ${sty.bg} ${sty.border} border-2 rounded-sm p-4`}
                            data-testid={`inbox-item-${item.type}`}
                        >
                            <div className="flex items-start gap-3">
                                <div className={`shrink-0 p-2 bg-white rounded-sm ${sty.icon}`}>
                                    <Icon className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className={`font-display font-black text-base ${sty.text}`}>{item.title}</h3>
                                    <p className="text-xs text-gray-700">{item.body}</p>
                                    {Array.isArray(item.items) && item.items.length > 0 && (
                                        <ul className="mt-2 space-y-1.5">
                                            {item.items.map((sub) => (
                                                <li key={sub.id} className="flex items-center gap-2 text-[12px] bg-white border border-gray-200 rounded-sm px-2.5 py-1.5" data-testid={`followup-item-${sub.id}`}>
                                                    <button
                                                        type="button"
                                                        onClick={() => navigate(`/edit-listing/${sub.listing_id}`)}
                                                        className="truncate flex-1 text-left hover:underline text-gray-800"
                                                        data-testid={`followup-open-${sub.id}`}
                                                    >
                                                        {sub.label}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => dismissFollowup(sub.id)}
                                                        className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900"
                                                        data-testid={`followup-done-${sub.id}`}
                                                    >
                                                        <CheckCircle2 className="w-3.5 h-3.5" /> Erledigt
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => navigate(item.cta_url)}
                                    className="shrink-0 flex items-center gap-1 text-xs font-bold uppercase tracking-wider opacity-70 hover:opacity-100"
                                    data-testid="followup-cta"
                                >
                                    {item.cta_label}
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    );
                }

                // Jun-2026 (User-Wunsch) — unvollständige Inserate: pro Inserat die
                // fehlenden Pflichtangaben + „Bearbeiten" + „Schlummern (2T/1W)".
                if (item.type === "incomplete_listings") {
                    const incTotal = Array.isArray(item.items) ? item.items.length : 0;
                    const incPages = Math.max(1, Math.ceil(incTotal / incPageSize));
                    const incSafePage = Math.min(incPage, incPages - 1);
                    const incVisible = (item.items || []).slice(incSafePage * incPageSize, (incSafePage + 1) * incPageSize);
                    return (
                        <div
                            key={item.type}
                            className={`w-full ${sty.bg} ${sty.border} border-2 rounded-sm p-4`}
                            data-testid={`inbox-item-${item.type}`}
                        >
                            <div className="flex items-start gap-3">
                                <div className={`shrink-0 p-2 bg-white rounded-sm ${sty.icon}`}>
                                    <Icon className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className={`font-display font-black text-base ${sty.text}`}>{item.title}</h3>
                                    <p className="text-xs text-gray-700">{item.body}</p>
                                    {incTotal > 0 && (
                                        <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="incomplete-pagination-controls">
                                            <span className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">Pro Seite:</span>
                                            {[10, 20, 50].map((n) => (
                                                <button
                                                    key={n}
                                                    type="button"
                                                    onClick={() => { setIncPageSize(n); setIncPage(0); }}
                                                    className={`text-[10px] font-bold px-2 py-0.5 rounded-sm border ${incPageSize === n ? "bg-[#111827] text-[#FFCC00] border-[#111827]" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
                                                    data-testid={`incomplete-pagesize-${n}`}
                                                >
                                                    {n}
                                                </button>
                                            ))}
                                            <span className="text-[10px] text-gray-500 font-mono-num ml-1" data-testid="incomplete-page-info">
                                                {incSafePage * incPageSize + 1}–{Math.min((incSafePage + 1) * incPageSize, incTotal)} von {incTotal} (älteste zuerst)
                                            </span>
                                            {incPages > 1 && (
                                                <span className="inline-flex items-center gap-1">
                                                    <button
                                                        type="button"
                                                        disabled={incSafePage === 0}
                                                        onClick={() => setIncPage(incSafePage - 1)}
                                                        className="text-[10px] font-bold px-2 py-0.5 rounded-sm border border-gray-300 bg-white disabled:opacity-40"
                                                        data-testid="incomplete-page-prev"
                                                    >
                                                        ‹
                                                    </button>
                                                    <span className="text-[10px] font-mono-num text-gray-600">{incSafePage + 1}/{incPages}</span>
                                                    <button
                                                        type="button"
                                                        disabled={incSafePage >= incPages - 1}
                                                        onClick={() => setIncPage(incSafePage + 1)}
                                                        className="text-[10px] font-bold px-2 py-0.5 rounded-sm border border-gray-300 bg-white disabled:opacity-40"
                                                        data-testid="incomplete-page-next"
                                                    >
                                                        ›
                                                    </button>
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    {incVisible.length > 0 && (
                                        <ul className="mt-2 space-y-1.5">
                                            {incVisible.map((sub) => (
                                                <li key={sub.id} className="bg-white border border-gray-200 rounded-sm px-2.5 py-2" data-testid={`incomplete-item-${sub.id}`}>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <button
                                                            type="button"
                                                            onClick={() => navigate(`/edit-listing/${sub.listing_id}`)}
                                                            className="font-bold text-[12px] text-gray-900 hover:underline text-left truncate"
                                                            data-testid={`incomplete-open-${sub.id}`}
                                                        >
                                                            {sub.label}
                                                        </button>
                                                        <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-gray-100 text-gray-600">
                                                            {sub.category === "motorcycle" ? "Motorrad" : "Fahrrad"}
                                                        </span>
                                                    </div>
                                                    {Array.isArray(sub.missing) && sub.missing.length > 0 && (
                                                        <div className="mt-1 flex flex-wrap gap-1" data-testid={`incomplete-missing-${sub.id}`}>
                                                            {sub.missing.map((m, i) => (
                                                                <span key={i} className="inline-flex items-center text-[10px] font-bold text-red-800 bg-red-50 border border-red-200 rounded-sm px-1.5 py-0.5">{m}</span>
                                                            ))}
                                                        </div>
                                                    )}
                                                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                                                        <button
                                                            type="button"
                                                            onClick={() => navigate(`/edit-listing/${sub.listing_id}`)}
                                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#111827] hover:text-black border border-gray-300 rounded-sm px-2 py-1 bg-white"
                                                            data-testid={`incomplete-edit-${sub.id}`}
                                                        >
                                                            <Pencil className="w-3 h-3" /> Bearbeiten
                                                        </button>
                                                        {assignees.length > 0 && (
                                                            <select
                                                                defaultValue=""
                                                                disabled={assignBusy === sub.listing_id}
                                                                onChange={(e) => { assignIncomplete(sub, e.target.value); e.target.value = ""; }}
                                                                className="h-[26px] text-[10px] font-bold uppercase tracking-wider rounded-sm border border-gray-300 bg-white px-1.5 text-gray-700 cursor-pointer"
                                                                data-testid={`incomplete-assign-${sub.listing_id}`}
                                                            >
                                                                <option value="" disabled>Zuweisen an …</option>
                                                                {assignees.map((a) => (
                                                                    <option key={a.id} value={a.id}>{a.name || a.email}</option>
                                                                ))}
                                                            </select>
                                                        )}
                                                        <span className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">Schlummern:</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => snoozeIncomplete(sub.listing_id, 2)}
                                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 hover:text-amber-900 border border-amber-300 rounded-sm px-2 py-1 bg-white"
                                                            data-testid={`incomplete-snooze-2-${sub.id}`}
                                                        >
                                                            <Clock className="w-3 h-3" /> 2 Tage
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => snoozeIncomplete(sub.listing_id, 7)}
                                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 hover:text-amber-900 border border-amber-300 rounded-sm px-2 py-1 bg-white"
                                                            data-testid={`incomplete-snooze-7-${sub.id}`}
                                                        >
                                                            <Clock className="w-3 h-3" /> 1 Woche
                                                        </button>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                }

                // Jun-2026 (User-Wunsch) — Dispo-To-Dos in „Was zu tun ist", inkl.
                // Inline-Erledigen (Abmeldung/HU-Datum) + Öffnen.
                if (item.type === "dispo_todos") {
                    return (
                        <div
                            key={item.type}
                            className={`w-full ${sty.bg} ${sty.border} border-2 rounded-sm p-4`}
                            data-testid={`inbox-item-${item.type}`}
                        >
                            <div className="flex items-start gap-3">
                                <div className={`shrink-0 p-2 bg-white rounded-sm ${sty.icon}`}>
                                    <Icon className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className={`font-display font-black text-base ${sty.text} flex items-center gap-2`}>
                                        {item.title}
                                        {/* Jul-2026 (User-Wunsch) — Bedingungen neu prüfen (z. B. geänderte Zahlungsart). */}
                                        <button type="button" onClick={load}
                                            title="Aktualisieren — alle Erinnerungen neu prüfen"
                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-gray-500 hover:text-[#111827]"
                                            data-testid="dispo-todos-refresh-btn">
                                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Aktualisieren
                                        </button>
                                    </h3>
                                    <p className="text-xs text-gray-700">{item.body}</p>
                                    {Array.isArray(item.todos) && item.todos.length > 0 && (
                                        <ul className="mt-2 space-y-1.5">
                                            {item.todos.map((t) => {
                                                const hasDone = t.action === "deregister" || t.action === "hu_renew";
                                                const isBonus = t.type === "hersteller_aktion";
                                                return (
                                                    <li key={t.id} className="bg-white border border-gray-200 rounded-sm px-2.5 py-2" data-testid={`dispo-todo-${t.id}`}>
                                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                                            <div className="min-w-0">
                                                                <div className="font-bold text-[12px] text-gray-900">{t.title}</div>
                                                                <div className="text-[11px] text-gray-500 truncate">{t.subtitle}</div>
                                                            </div>
                                                            <div className="flex items-center gap-2 shrink-0">
                                                                {/* Jul-2026 (User-Wunsch) — Schlummern wie bei den anderen Meldungen. */}
                                                                <span className="inline-flex items-center gap-1 text-[10px] text-gray-400">
                                                                    <CalendarClock className="w-3 h-3" />
                                                                    <button type="button" onClick={() => dispoSnooze(t, 2)} disabled={dispoBusy === t.id}
                                                                        className="font-bold uppercase tracking-wider hover:text-[#111827] disabled:opacity-50"
                                                                        title="2 Tage schlummern" data-testid={`dispo-todo-snooze-2d-${t.id}`}>2T</button>
                                                                    <span>·</span>
                                                                    <button type="button" onClick={() => dispoSnooze(t, 7)} disabled={dispoBusy === t.id}
                                                                        className="font-bold uppercase tracking-wider hover:text-[#111827] disabled:opacity-50"
                                                                        title="1 Woche schlummern" data-testid={`dispo-todo-snooze-1w-${t.id}`}>1W</button>
                                                                </span>
                                                                {isBonus ? (
                                                                    <>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => extendBonus(t)}
                                                                            disabled={dispoBusy === t.id}
                                                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 border border-emerald-300 rounded-sm px-2 py-1 bg-white disabled:opacity-50"
                                                                            data-testid={`dispo-todo-extend-${t.id}`}
                                                                        >
                                                                            {dispoBusy === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Verlängern
                                                                        </button>
                                                                        {t.action_url && (
                                                                            <a href={t.action_url} target="_blank" rel="noopener noreferrer"
                                                                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-sky-700 hover:text-sky-900 border border-sky-200 rounded-sm px-2 py-1 bg-white"
                                                                                data-testid={`dispo-todo-action-url-${t.id}`}>
                                                                                Aktion
                                                                            </a>
                                                                        )}
                                                                        <a href="/dashboard?tab=settings"
                                                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#111827] hover:text-black border border-gray-300 rounded-sm px-2 py-1 bg-white"
                                                                            data-testid={`dispo-todo-open-${t.id}`}>
                                                                            Bearbeiten
                                                                        </a>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {hasDone && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => { setDispoActiveId((c) => (c === t.id ? null : t.id)); setDispoDate(""); }}
                                                                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 border border-emerald-300 rounded-sm px-2 py-1 bg-white"
                                                                                data-testid={`dispo-todo-done-${t.id}`}
                                                                            >
                                                                                <CheckCircle2 className="w-3 h-3" /> Erledigen
                                                                            </button>
                                                                        )}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => window.open(`/edit-listing/${t.listing_id}?focus=disposition`, "_blank", "noopener")}
                                                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#111827] hover:text-black border border-gray-300 rounded-sm px-2 py-1 bg-white"
                                                                            data-testid={`dispo-todo-open-${t.id}`}
                                                                        >
                                                                            Öffnen
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {dispoActiveId === t.id && (
                                                            <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2" data-testid={`dispo-todo-form-${t.id}`}>
                                                                <span className="text-[10px] font-bold text-gray-600">{t.action === "hu_renew" ? "Neues HU/TÜV-Datum:" : "Abmeldedatum (optional):"}</span>
                                                                <input
                                                                    type={t.action === "hu_renew" ? "month" : "date"}
                                                                    value={dispoDate}
                                                                    onChange={(e) => setDispoDate(e.target.value)}
                                                                    className="h-8 rounded-sm border border-gray-300 px-2 text-xs bg-white"
                                                                    data-testid={`dispo-todo-date-${t.id}`}
                                                                />
                                                                <button
                                                                    type="button"
                                                                    onClick={() => dispoConfirm(t)}
                                                                    disabled={dispoBusy === t.id}
                                                                    className="inline-flex items-center gap-1 h-8 px-3 rounded-sm bg-[#111827] hover:bg-black text-white text-[10px] font-bold uppercase tracking-wider disabled:opacity-50"
                                                                    data-testid={`dispo-todo-confirm-${t.id}`}
                                                                >
                                                                    {dispoBusy === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Erledigt
                                                                </button>
                                                            </div>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                }

                // Jun-2026 (User-Wunsch) — von Mitarbeitern erledigte Aufgaben:
                // Händler-Benachrichtigung mit „Zur Kenntnis"-Button je Aufgabe.
                if (item.type === "delegated_done") {
                    return (
                        <div
                            key={item.type}
                            className={`w-full ${sty.bg} ${sty.border} border-2 rounded-sm p-4`}
                            data-testid={`inbox-item-${item.type}`}
                        >
                            <div className="flex items-start gap-3">
                                <div className={`shrink-0 p-2 bg-white rounded-sm ${sty.icon}`}>
                                    <Icon className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className={`font-display font-black text-base ${sty.text}`}>{item.title}</h3>
                                    <p className="text-xs text-gray-700">{item.body}</p>
                                    {Array.isArray(item.items) && item.items.length > 0 && (
                                        <ul className="mt-2 space-y-1.5">
                                            {item.items.map((sub) => (
                                                <li key={sub.id} className="bg-white border border-gray-200 rounded-sm px-2.5 py-2 flex items-center justify-between gap-2" data-testid={`done-task-${sub.id}`}>
                                                    <span className="text-[12px] font-bold text-gray-900 truncate flex items-center gap-1">
                                                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> {sub.label}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={() => ackTask(sub.id)}
                                                        className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#111827] hover:text-black border border-gray-300 rounded-sm px-2 py-1 bg-white"
                                                        data-testid={`done-task-ack-${sub.id}`}
                                                    >
                                                        Zur Kenntnis
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                }

                // Jul-2026 (User-Wunsch) — Standzeiten-Warner: Standgebühren-Rechnung per Klick.
                if (item.type === "standzeit_warning") {
                    return (
                        <div key={item.type} className={`w-full ${sty.bg} ${sty.border} border-2 rounded-sm p-4`} data-testid={`inbox-item-${item.type}`}>
                            <div className="flex items-start gap-3">
                                <div className={`shrink-0 p-2 bg-white rounded-sm ${sty.icon}`}><Icon className="w-5 h-5" /></div>
                                <div className="flex-1 min-w-0">
                                    <h3 className={`font-display font-black text-base ${sty.text}`}>{item.title}</h3>
                                    <p className="text-xs text-gray-700">{item.body}</p>
                                    {Array.isArray(item.items) && item.items.length > 0 && (
                                        <ul className="mt-2 space-y-1.5">
                                            {item.items.map((sub) => (
                                                <li key={sub.id} className="bg-white border border-gray-200 rounded-sm px-2.5 py-2 flex items-center justify-between gap-2 flex-wrap" data-testid={`standzeit-item-${sub.id}`}>
                                                    <span className="text-[12px] font-bold text-gray-900 truncate">{sub.label}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => createStandInvoice(sub)}
                                                        disabled={standBusy === sub.id || !sub.fee_eur}
                                                        className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#111827] hover:text-black border border-gray-300 rounded-sm px-2 py-1 bg-white disabled:opacity-50"
                                                        title="Erstellt eine Standgebühren-Rechnung (Entwurf) für den Kunden des Auftrags"
                                                        data-testid={`standzeit-invoice-${sub.id}`}
                                                    >
                                                        {standBusy === sub.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />} Rechnung erstellen
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                }

                // Jul-2026 (User-Wunsch) — Sofortmitnahme: Filialleiter-Freigabe direkt aus der Inbox.
                if (item.type === "sofortmitnahme_approval") {
                    return (
                        <div key={item.type} className={`w-full ${sty.bg} ${sty.border} border-2 rounded-sm p-4`} data-testid={`inbox-item-${item.type}`}>
                            <div className="flex items-start gap-3">
                                <div className={`shrink-0 p-2 bg-white rounded-sm ${sty.icon}`}><Icon className="w-5 h-5" /></div>
                                <div className="flex-1 min-w-0">
                                    <h3 className={`font-display font-black text-base ${sty.text}`}>{item.title}</h3>
                                    <p className="text-xs text-gray-700">{item.body}</p>
                                    {Array.isArray(item.items) && item.items.length > 0 && (
                                        <ul className="mt-2 space-y-1.5">
                                            {item.items.map((sub) => (
                                                <li key={sub.id} className="bg-white border border-gray-200 rounded-sm px-2.5 py-2" data-testid={`sofortmitnahme-inbox-${sub.id}`}>
                                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                                        <div className="min-w-0">
                                                            <div className="font-bold text-[12px] text-gray-900 truncate">{sub.label}</div>
                                                            {sub.note && <div className="text-[11px] text-gray-500"><span className="font-semibold">Notiz:</span> {sub.note}</div>}
                                                        </div>
                                                        <div className="flex items-center gap-1.5 shrink-0">
                                                            <button
                                                                type="button"
                                                                onClick={() => decideSofort(sub, true)}
                                                                disabled={sofortBusy === sub.id}
                                                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white bg-emerald-600 hover:bg-emerald-700 rounded-sm px-2 py-1 disabled:opacity-50"
                                                                data-testid={`sofortmitnahme-inbox-approve-${sub.id}`}
                                                            >
                                                                {sofortBusy === sub.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Freigeben
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => decideSofort(sub, false)}
                                                                disabled={sofortBusy === sub.id}
                                                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-red-700 border border-red-300 rounded-sm px-2 py-1 bg-white disabled:opacity-50"
                                                                data-testid={`sofortmitnahme-inbox-reject-${sub.id}`}
                                                            >
                                                                Ablehnen
                                                            </button>
                                                        </div>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                }

                // Jul-2026 (User-Wunsch) — Link-Reparatur direkt aus dem Hinweis heraus.
                if (item.type === "broken_links") {
                    return (
                        <div key={item.type} className={`w-full ${sty.bg} ${sty.border} border-2 rounded-sm p-4`} data-testid={`inbox-item-${item.type}`}>
                            <div className="flex items-start gap-3">
                                <div className={`shrink-0 p-2 bg-white rounded-sm ${sty.icon}`}><Icon className="w-5 h-5" /></div>
                                <div className="flex-1 min-w-0">
                                    <h3 className={`font-display font-black text-base ${sty.text}`}>{item.title}</h3>
                                    <p className="text-xs text-gray-700">{item.body}</p>
                                    {Array.isArray(item.items) && item.items.length > 0 && (
                                        <ul className="mt-2 space-y-1.5">
                                            {item.items.map((sub) => (
                                                <li key={sub.id} className="bg-white border border-gray-200 rounded-sm px-2.5 py-2" data-testid={`broken-link-${sub.id}`}>
                                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                                        <span className="text-[12px] font-bold text-gray-900 truncate min-w-0">{sub.label}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => { setLinkEditId(linkEditId === sub.id ? null : sub.id); setLinkNewUrl(""); }}
                                                            className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#111827] hover:text-black border border-gray-300 rounded-sm px-2 py-1 bg-white"
                                                            data-testid={`broken-link-edit-${sub.id}`}
                                                        >
                                                            Korrigieren
                                                        </button>
                                                    </div>
                                                    {linkEditId === sub.id && (
                                                        <div className="mt-2 border-t border-gray-100 pt-2 flex items-center gap-2 flex-wrap" data-testid={`broken-link-form-${sub.id}`}>
                                                            <input
                                                                type="url"
                                                                value={linkNewUrl}
                                                                onChange={(e) => setLinkNewUrl(e.target.value)}
                                                                placeholder="https://neuer-link.de"
                                                                className="h-8 flex-1 min-w-[180px] rounded-sm border border-gray-300 px-2 text-xs bg-white"
                                                                data-testid={`broken-link-input-${sub.id}`}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => fixLink(sub, false)}
                                                                disabled={linkBusy === sub.id || !linkNewUrl.trim()}
                                                                className="inline-flex items-center gap-1 h-8 px-3 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold uppercase tracking-wider disabled:opacity-50"
                                                                data-testid={`broken-link-save-${sub.id}`}
                                                            >
                                                                {linkBusy === sub.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Speichern"}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => fixLink(sub, true)}
                                                                disabled={linkBusy === sub.id}
                                                                className="inline-flex items-center gap-1 h-8 px-3 rounded-sm border border-red-300 text-red-700 bg-white text-[10px] font-bold uppercase tracking-wider disabled:opacity-50"
                                                                data-testid={`broken-link-remove-${sub.id}`}
                                                            >
                                                                Link entfernen
                                                            </button>
                                                        </div>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                }

                // Aug-2026 (User-Wunsch) — Probefahrt-Anfragen: einzeln gelistet,
                // jede Anfrage einzeln zuweisbar + bestätigen/ablehnen inline.
                if (item.type === "pending_testrides") {
                    return (
                        <div key={item.type} className={`w-full ${sty.bg} ${sty.border} border-2 rounded-sm p-4`} data-testid={`inbox-item-${item.type}`}>
                            <div className="flex items-start gap-3">
                                <div className={`shrink-0 p-2 bg-white rounded-sm ${sty.icon}`}><Icon className="w-5 h-5" /></div>
                                <div className="flex-1 min-w-0">
                                    <h3 className={`font-display font-black text-base ${sty.text}`}>{item.title}</h3>
                                    <p className="text-xs text-gray-700">{item.body}</p>
                                    {Array.isArray(item.items) && item.items.length > 0 && (
                                        <ul className="mt-2 space-y-1.5">
                                            {item.items.map((sub) => (
                                                <li key={sub.id} className="bg-white border border-gray-200 rounded-sm px-2.5 py-2" data-testid={`testride-item-${sub.id}`}>
                                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                                        <div className="min-w-0">
                                                            <button
                                                                type="button"
                                                                onClick={() => sub.listing_id && window.open(`/listing/${sub.listing_id}`, "_blank", "noopener")}
                                                                className="font-bold text-[12px] text-gray-900 hover:underline text-left truncate"
                                                                data-testid={`testride-open-${sub.id}`}
                                                            >
                                                                {sub.label}
                                                            </button>
                                                            <div className="text-[11px] text-gray-500">
                                                                {sub.preferred_date ? `Wunschtermin: ${sub.preferred_date}` : "Kein Wunschtermin"}
                                                                {sub.status === "rescheduled" ? " · verschoben" : ""}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                                                            {employees.length > 0 && (
                                                                <select
                                                                    value={sub.assigned_seller_id || ""}
                                                                    disabled={trBusy === sub.id}
                                                                    onChange={(e) => assignTestRide(sub, e.target.value)}
                                                                    className="h-7 text-[11px] border border-gray-300 rounded-sm px-1.5 bg-white max-w-[160px]"
                                                                    title="Anfrage einem Mitarbeiter zuweisen"
                                                                    data-testid={`testride-assign-${sub.id}`}
                                                                >
                                                                    <option value="">— zuweisen —</option>
                                                                    {employees.map((emp) => (
                                                                        <option key={emp.id} value={emp.id}>{emp.name}</option>
                                                                    ))}
                                                                </select>
                                                            )}
                                                            <button
                                                                type="button"
                                                                onClick={() => testRideStatus(sub, "confirmed")}
                                                                disabled={trBusy === sub.id}
                                                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 border border-emerald-300 rounded-sm px-2 py-1 bg-white disabled:opacity-50"
                                                                data-testid={`testride-confirm-${sub.id}`}
                                                            >
                                                                {trBusy === sub.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Bestätigen
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => testRideStatus(sub, "cancelled")}
                                                                disabled={trBusy === sub.id}
                                                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-red-700 border border-red-300 rounded-sm px-2 py-1 bg-white disabled:opacity-50"
                                                                data-testid={`testride-decline-${sub.id}`}
                                                            >
                                                                Ablehnen
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {sub.assigned_seller_name && (
                                                        <div className="mt-1 text-[10px] font-bold text-sky-700" data-testid={`testride-assigned-${sub.id}`}>
                                                            Zugewiesen an: {sub.assigned_seller_name}
                                                        </div>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                }

                // Jun-2026 (User-Wunsch) — Kundendienst-Freigabe für vom Monteur
                // verschobene Fertigstellungstermine: prüfen, Datum/Grund anpassen,
                // freigeben (Kundeninfo geht raus) oder verwerfen.
                if (item.type === "workorder_reschedule_approval") {
                    return (
                        <div
                            key={item.type}
                            className={`w-full ${sty.bg} ${sty.border} border-2 rounded-sm p-4`}
                            data-testid={`inbox-item-${item.type}`}
                        >
                            <div className="flex items-start gap-3">
                                <div className={`shrink-0 p-2 bg-white rounded-sm ${sty.icon}`}>
                                    <Icon className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className={`font-display font-black text-base ${sty.text}`}>{item.title}</h3>
                                    <p className="text-xs text-gray-700">{item.body}</p>
                                    {Array.isArray(item.items) && item.items.length > 0 && (
                                        <ul className="mt-2 space-y-1.5">
                                            {item.items.map((sub) => (
                                                <li key={sub.id} className="bg-white border border-gray-200 rounded-sm px-2.5 py-2" data-testid={`reschedule-item-${sub.id}`}>
                                                    <div className="flex items-start justify-between gap-2 flex-wrap">
                                                        <div className="min-w-0">
                                                            <div className="font-bold text-[12px] text-gray-900 truncate">{sub.label}</div>
                                                            <div className="text-[11px] text-gray-600">
                                                                {sub.old_date ? `${sub.old_date.split("-").reverse().join(".")} → ` : ""}
                                                                <span className="font-bold">{(sub.new_date || "").split("-").reverse().join(".")}</span>
                                                                {sub.requested_by_name ? ` · ${sub.requested_by_name}` : ""}
                                                            </div>
                                                            {sub.reason && (
                                                                <div className="text-[11px] text-gray-500 mt-0.5"><span className="font-semibold">Grund:</span> {sub.reason}</div>
                                                            )}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => openApproval(sub)}
                                                            className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#111827] hover:text-black border border-gray-300 rounded-sm px-2 py-1 bg-white"
                                                            data-testid={`reschedule-open-${sub.id}`}
                                                        >
                                                            Prüfen
                                                        </button>
                                                    </div>
                                                    {apprActiveId === sub.id && (
                                                        <div className="mt-2 border-t border-gray-100 pt-2 space-y-2" data-testid={`reschedule-form-${sub.id}`}>
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Termin korrigieren:</span>
                                                                <input
                                                                    type="date"
                                                                    value={apprDate}
                                                                    onChange={(e) => setApprDate(e.target.value)}
                                                                    className="h-8 rounded-sm border border-gray-300 px-2 text-xs bg-white"
                                                                    data-testid={`reschedule-date-${sub.id}`}
                                                                />
                                                            </div>
                                                            <div>
                                                                <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Grund (an Kunde) ergänzen/anpassen</label>
                                                                <textarea
                                                                    value={apprReason}
                                                                    onChange={(e) => setApprReason(e.target.value)}
                                                                    rows={2}
                                                                    className="w-full rounded-sm border border-gray-300 px-2 py-1 text-xs bg-white mt-0.5"
                                                                    data-testid={`reschedule-reason-${sub.id}`}
                                                                />
                                                            </div>
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => approveReschedule(sub)}
                                                                    disabled={apprBusy === sub.id || !apprReason.trim()}
                                                                    className="inline-flex items-center gap-1 h-8 px-3 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold uppercase tracking-wider disabled:opacity-50"
                                                                    data-testid={`reschedule-approve-${sub.id}`}
                                                                >
                                                                    {apprBusy === sub.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Freigeben & Kunde informieren
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => dismissReschedule(sub)}
                                                                    disabled={apprBusy === sub.id}
                                                                    className="inline-flex items-center gap-1 h-8 px-3 rounded-sm border border-gray-300 text-gray-700 hover:bg-gray-50 text-[10px] font-bold uppercase tracking-wider disabled:opacity-50"
                                                                    data-testid={`reschedule-dismiss-${sub.id}`}
                                                                >
                                                                    Verwerfen (keine Info)
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                }

                // Jul-2026 (User-Wunsch) — ungelesene Nachrichten: jeder Vorgang
                // einzeln gelistet, einzeln zu öffnen/zuzuweisen; inkl. Archiv-
                // Querverweisen (Fahrzeug + Kunde, der das Fahrzeug aktuell hat).
                if (item.type === "unread_messages") {
                    return (
                        <div key={item.type} className={`w-full ${sty.bg} ${sty.border} border-2 rounded-sm p-4`} data-testid={`inbox-item-${item.type}`}>
                            <div className="flex items-start gap-3">
                                <div className={`shrink-0 p-2 bg-white rounded-sm ${sty.icon}`}><Icon className="w-5 h-5" /></div>
                                <div className="flex-1 min-w-0">
                                    <h3 className={`font-display font-black text-base ${sty.text}`}>{item.title}</h3>
                                    <p className="text-xs text-gray-700">{item.body}</p>
                                    {Array.isArray(item.items) && item.items.length > 0 && (
                                        <ul className="mt-2 space-y-1.5">
                                            {item.items.map((sub) => (
                                                <li key={sub.id} className="bg-white border border-gray-200 rounded-sm px-2.5 py-2" data-testid={`msg-item-${sub.id}`}>
                                                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                                                        <button
                                                            type="button"
                                                            onClick={() => navigate(`/dashboard?tab=messages&thread=${sub.thread_id}`)}
                                                            className="font-bold text-[12px] text-gray-900 hover:underline text-left truncate"
                                                            data-testid={`msg-open-${sub.id}`}
                                                        >
                                                            {sub.sender}
                                                        </button>
                                                        {sub.guest && <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 rounded-sm px-1.5 py-0.5">Gast</span>}
                                                        {sub.count > 1 && <span className="text-[10px] font-bold bg-red-100 text-red-700 rounded-sm px-1.5 py-0.5 font-mono-num">{sub.count} neu</span>}
                                                        {sub.listing_title && <span className="text-[11px] text-gray-500 truncate">· {sub.listing_title}</span>}
                                                    </div>
                                                    {sub.snippet && <div className="text-[11px] text-gray-500 truncate mt-0.5" data-testid={`msg-snippet-${sub.id}`}>„{sub.snippet}“</div>}
                                                    <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                                                        <button
                                                            type="button"
                                                            onClick={() => navigate(`/dashboard?tab=messages&thread=${sub.thread_id}`)}
                                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white bg-[#111827] hover:bg-black rounded-sm px-2 py-1"
                                                            data-testid={`msg-reply-${sub.id}`}
                                                        >
                                                            <MessageSquare className="w-3 h-3" /> Öffnen & antworten
                                                        </button>
                                                        {sub.listing_id && (
                                                            <button
                                                                type="button"
                                                                onClick={() => navigate(`/edit-listing/${sub.listing_id}?focus=archiv`)}
                                                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#111827] hover:text-black border border-gray-300 rounded-sm px-2 py-1 bg-white"
                                                                title="Direkt zum Dokumenten-Archiv des Fahrzeugs"
                                                                data-testid={`msg-vehicle-archive-${sub.id}`}
                                                            >
                                                                <FileText className="w-3 h-3" /> Fahrzeug-Archiv
                                                            </button>
                                                        )}
                                                        {sub.customer_q && (
                                                            <button
                                                                type="button"
                                                                onClick={() => navigate(`/dealer/invoices?tab=customers&q=${encodeURIComponent(sub.customer_q)}&archiv=1`)}
                                                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#111827] hover:text-black border border-gray-300 rounded-sm px-2 py-1 bg-white"
                                                                title={`Archiv des Kunden öffnen, der das Fahrzeug aktuell hat${sub.customer_name ? ` (${sub.customer_name})` : ""}`}
                                                                data-testid={`msg-customer-archive-${sub.id}`}
                                                            >
                                                                <User className="w-3 h-3" /> Kunden-Archiv{sub.customer_name ? `: ${sub.customer_name}` : ""}
                                                            </button>
                                                        )}
                                                        {assignees.length > 0 && (
                                                            <select
                                                                defaultValue=""
                                                                disabled={msgAssignBusy === sub.id}
                                                                onChange={(e) => { assignMessage(sub, e.target.value); e.target.value = ""; }}
                                                                className="h-[26px] text-[10px] font-bold uppercase tracking-wider rounded-sm border border-gray-300 bg-white px-1.5 text-gray-700 cursor-pointer"
                                                                data-testid={`msg-assign-${sub.id}`}
                                                            >
                                                                <option value="" disabled>Zuweisen an …</option>
                                                                {assignees.map((a) => (
                                                                    <option key={a.id} value={a.id}>{a.name}{a.absent ? ` (${a.absent_label || "abwesend"})` : ""}</option>
                                                                ))}
                                                            </select>
                                                        )}
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => navigate(item.cta_url)}
                                    className="shrink-0 flex items-center gap-1 text-xs font-bold uppercase tracking-wider opacity-70 hover:opacity-100"
                                    data-testid="msgs-open-all"
                                >
                                    {item.cta_label}
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    );
                }

                // Aug-2026 (User-Report Jörg) — Werkstatt-Termine fällig: je Auftrag
                // inline auflösbar (z. B. wenn der Termin nicht mehr existiert).
                if (item.type === "workshop_due_soon") {
                    return (
                        <div key={item.type} className={`w-full ${sty.bg} ${sty.border} border-2 rounded-sm p-4`} data-testid={`inbox-item-${item.type}`}>
                            <div className="flex items-start gap-3">
                                <div className={`shrink-0 p-2 bg-white rounded-sm ${sty.icon}`}>
                                    <Icon className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                        <h3 className={`font-display font-black text-base ${sty.text}`}>{item.title}</h3>
                                        {item.severity === "critical" && (
                                            <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm ${sty.pill}`}>Dringend</span>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-700">{item.body}</p>
                                    <ul className="mt-2 space-y-1.5">
                                        {(item.items || []).map((sub) => (
                                            <li key={sub.id} className="bg-white border border-gray-200 rounded-sm px-2.5 py-2 flex items-center justify-between gap-2 flex-wrap" data-testid={`workshop-due-${sub.id}`}>
                                                <span className="text-[12px] text-gray-900 min-w-0 truncate">• {sub.label}</span>
                                                <span className="flex items-center gap-2 shrink-0">
                                                    <button type="button" onClick={() => resolveWorkshopDue(sub)} disabled={dispoBusy === sub.id}
                                                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 border border-emerald-300 rounded-sm px-2 py-1 bg-white disabled:opacity-50"
                                                        title="Termin-Hinweis auflösen — verschwindet aus dieser Liste"
                                                        data-testid={`workshop-due-resolve-${sub.id}`}>
                                                        {dispoBusy === sub.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Erledigt
                                                    </button>
                                                    <button type="button" onClick={() => navigate(`/dealer/workshop-orders?open=${sub.id}`)}
                                                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#111827] hover:text-black border border-gray-300 rounded-sm px-2 py-1 bg-white"
                                                        data-testid={`workshop-due-open-${sub.id}`}>
                                                        Öffnen
                                                    </button>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    );
                }

                return (
                    <div
                        key={item.type}
                        className={`w-full text-left ${sty.bg} ${sty.border} border-2 rounded-sm p-4 hover:shadow-md transition-shadow group`}
                        data-testid={`inbox-item-${item.type}`}
                    >
                        <div className="flex items-start gap-3">
                        <button
                            type="button"
                            onClick={() => navigate(item.cta_url)}
                            className="flex items-start gap-3 flex-1 min-w-0 text-left"
                            data-testid={`inbox-open-${item.type}`}
                        >
                            <div className={`shrink-0 p-2 bg-white rounded-sm ${sty.icon}`}>
                                <Icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                <h3 className={`font-display font-black text-base ${sty.text}`}>{item.title}</h3>
                                {item.severity === "critical" && (
                                    <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm ${sty.pill}`}>Dringend</span>
                                )}
                                {isReactivation && item.meta?.uplift_pct != null && (
                                    <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm ${sty.pill} font-mono-num`} data-testid="inbox-reactivation-uplift">
                                        +{Number(item.meta.uplift_pct).toFixed(1)} %
                                    </span>
                                )}
                                {isReactivation && (
                                    <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-[#111827] text-[#FFCC00]">KI-Tipp</span>
                                )}
                            </div>
                            {/* Jul-2026 (User-Wunsch) — überfällige delegierte Aufgaben: nur die
                                Titelzeile zeigen; Klick springt direkt zu den Aufgaben. */}
                            {item.type !== "delegated_overdue" && <p className="text-xs text-gray-700">{item.body}</p>}
                            {isReactivation && item.meta && (item.meta.asking_price || item.meta.median_sold_price) && (
                                <div className="mt-2 flex items-center gap-3 text-[11px] text-gray-700 font-mono-num">
                                    {item.meta.asking_price ? (
                                        <span>Festpreis: <strong>{item.meta.asking_price.toLocaleString("de-DE")} €</strong></span>
                                    ) : null}
                                    {item.meta.median_sold_price ? (
                                        <span>⌀ Verkauf 30 T: <strong>{item.meta.median_sold_price.toLocaleString("de-DE")} €</strong></span>
                                    ) : null}
                                    {item.meta.sold_count_30d ? (
                                        <span>{item.meta.sold_count_30d}× verkauft</span>
                                    ) : null}
                                </div>
                            )}
                            {Array.isArray(item.items) && item.items.length > 0 && item.type !== "delegated_overdue" && !item.items.some((s) => s.seller_name) && (
                                <ul className="mt-2 text-[11px] text-gray-700 space-y-0.5">
                                    {item.items.slice(0, 3).map((sub, idx) => (
                                        <li key={`${sub.id ?? "x"}-${idx}`} className="truncate">• {sub.label}</li>
                                    ))}
                                    {item.items.length > 3 && <li className="text-gray-500">+ {item.items.length - 3} weitere</li>}
                                </ul>
                            )}
                        </div>
                        </button>
                        <div className="shrink-0 flex flex-col items-end gap-2">
                            <button
                                type="button"
                                onClick={() => navigate(item.cta_url)}
                                className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider opacity-70 group-hover:opacity-100"
                            >
                                {item.cta_label}
                                <ChevronRight className="w-4 h-4" />
                            </button>
                            {item.type !== "delegated_overdue" && (
                                <button
                                    type="button"
                                    onClick={() => setAssignItem({ title: item.title, body: item.body, type: item.type, cta_url: item.cta_url })}
                                    className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#111827] border border-gray-300 rounded-sm px-2 py-1 bg-white hover:border-[#FFCC00]"
                                    data-testid={`inbox-assign-${item.type}`}
                                    title="An Mitarbeiter zuweisen"
                                >
                                    <UserPlus className="w-3 h-3" /> Zuweisen
                                </button>
                            )}
                        </div>
                        </div>
                        {Array.isArray(item.items) && item.items.some((s) => s.seller_name) && (
                            <ul className="mt-3 pt-3 border-t border-gray-200/70 space-y-1.5" data-testid={`inbox-seller-list-${item.type}`}>
                                {item.items.filter((s) => s.seller_name).map((sub, idx) => (
                                    <li key={`as-${sub.id ?? sub.listing_id ?? idx}`} className="flex items-center gap-2 text-[11px] text-gray-700">
                                        <span className="flex-1 min-w-0 truncate">• {sub.label}</span>
                                        <AssignToSellerButton
                                            listingId={sub.listing_id}
                                            sellerName={sub.seller_name}
                                            title={`${item.title} · ${sub.label}`}
                                            ctaUrl={item.cta_url}
                                        />
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                );
            })}
            <AssignTaskDialog
                open={!!assignItem}
                item={assignItem}
                onClose={() => setAssignItem(null)}
                onAssigned={load}
            />
        </div>
    );
}
